// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { isAuthnWorkflowId } from '@imprivata-cloud/authn';
import { debug, ImprHeaders } from '@imprivata-cloud/common';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { completeIdpAuthn, setDefaultHeader } from '@/api/api';
import { QueryParam } from '@/constants';
import { InformationDialog } from '@/errorHandler/error-views/no-inline-enrollment';
import { getWorkflowId } from '@/tracing';
import { hasDisableAutoComplete } from '@/utils';

import {
  AuthnFlow,
  type LaunchConfig,
  type OutcomeReporter,
} from '../authn-flow';

type OidcLaunchRejection =
  | 'missing-context-id'
  | 'missing-tenant-id'
  | 'unknown-authn-workflow-id';

type OidcLaunchResult =
  | {
      ok: true;
      launchConfig: LaunchConfig;
      outcomeReporter: OutcomeReporter;
    }
  | { ok: false; reason: OidcLaunchRejection };

const completeIdpAuthnRedirect = async (
  contextId: string,
  error?: string,
  errorDescription?: string,
): Promise<string> => {
  const { redirectUri } = await completeIdpAuthn({
    contextId,
    error,
    errorDescription,
  });
  return redirectUri;
};

const idpAuthnCompleteReporter = (contextId: string): OutcomeReporter => ({
  // Epic is waiting on the outcome, so a denied push cancels the signing.
  failsOnPushDenied: true,
  // Only /idp-authn/complete needs workflow correlation.
  onMount: () => {
    const workflowId = getWorkflowId();
    if (workflowId) {
      setDefaultHeader(ImprHeaders.ImprWorkflowId, workflowId);
    }
  },
  // The authorization code is deliberately not sent: the backend determines
  // success from the server-side authn session bound to contextId.
  onSuccess: async () => {
    const redirectUri = await completeIdpAuthnRedirect(contextId);
    return { redirectUrl: redirectUri, spanAttrs: { redirectUri } };
  },
  onFailure: async ({ errorCode, errorDescription }) => {
    const redirectUri = await completeIdpAuthnRedirect(
      contextId,
      errorCode,
      errorDescription,
    );
    return {
      redirectUrl: redirectUri,
      spanAttrs: { redirectUri, errorCode, errorDescription },
    };
  },
});

// Epic never sends codeChallenge/acrValues/contextType/resourceType, so those
// LaunchConfig fields are fixed rather than read. loginHint is deliberately
// ignored too: Epic's value is the Epic username, not something the user can
// sign in with here, so it must not prefill or lock the username field. The
// backend uses it to check the authenticated user is the one Epic sent.
const readOidcLaunch = (search: string): OidcLaunchResult => {
  const queryParams = new URLSearchParams(search);

  const contextId = queryParams.get(QueryParam.ContextId);
  if (!contextId) {
    return { ok: false, reason: 'missing-context-id' };
  }
  const tenantId = queryParams.get(QueryParam.TenantId);
  if (!tenantId) {
    return { ok: false, reason: 'missing-tenant-id' };
  }
  const authnWorkflowId = queryParams.get(QueryParam.AuthnWorkflowId);
  if (!isAuthnWorkflowId(authnWorkflowId)) {
    return { ok: false, reason: 'unknown-authn-workflow-id' };
  }

  return {
    ok: true,
    outcomeReporter: idpAuthnCompleteReporter(contextId),
    launchConfig: {
      tenantId,
      authnWorkflowId,
      clientName: queryParams.get(QueryParam.ClientName) ?? undefined,
      applicationId: queryParams.get(QueryParam.ApplicationId) ?? undefined,
      hideFactorSwitch: !queryParams.get(QueryParam.ShowAddMethod),
      disableAutoComplete: hasDisableAutoComplete(search),
      contextResource: { resourceType: '', contextType: '' },
      oidcRequestData: { codeChallenge: '', codeChallengeMethod: '' },
      initialFactors: undefined,
      fitVideoFrame: undefined,
      loginHint: null,
      isOneToMany: false,
    },
  };
};

export const OidcFlow: React.FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  // Memoized on the search string: a new launchConfig or reporter identity
  // mid-ceremony would rebuild <Authn>'s API service and drop its session.
  const launch = useMemo(() => readOidcLaunch(search), [search]);

  if (!launch.ok) {
    debug('Rejected invalid /oidc launch', { reason: launch.reason });
    return <InformationDialog text={t('errors.something-unexpected')} />;
  }

  return (
    <AuthnFlow
      launchConfig={launch.launchConfig}
      showUI={true}
      outcomeReporter={launch.outcomeReporter}
    />
  );
};
